import { useState, useEffect } from "react";
import AgentLayout from "@/components/layout/AgentLayout";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { useLocation, useParams } from "wouter";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, queryClient } from "@/lib/queryClient";
import { apiRequest } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Copy, Link, Trash, Edit } from "lucide-react";
import { formatDistance } from "date-fns";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/use-auth";

export default function AgentLinksPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [location] = useLocation();
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<string>("");
  const [markupPercentage, setMarkupPercentage] = useState<string>("5");
  const [currentEditLink, setCurrentEditLink] = useState<any>(null);
  const [linkType, setLinkType] = useState<"specific" | "universal">("specific");
  
  // Parse URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const propertyId = params.get("propertyId");
    
    if (propertyId) {
      // Set propertyId from URL parameter
      setSelectedProperty(propertyId);
      setLinkType("specific");
      setIsCreatingLink(true);
    }
  }, []);

  // Fetch properties for selection
  const { data: properties, isLoading: isLoadingProperties } = useQuery({
    queryKey: ["properties"],
    queryFn: getQueryFn(),
  });

  // Fetch agent links
  const {
    data: links,
    isLoading: isLoadingLinks,
    isError: isLinksError,
  } = useQuery({
    queryKey: ["agent/links"],
    queryFn: getQueryFn(),
    // refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Create a new link
  const createLinkMutation = useMutation({
    mutationFn: async (newLink: { propertyId?: number; markupPercentage: number; allProperties?: boolean }) => {
      const res = await apiRequest('POST', 'agent/links', newLink);
      // return await res.json();
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent/links"] });
      setIsCreatingLink(false);
      setSelectedProperty("");
      setMarkupPercentage("5");
      setLinkType("specific");
      toast({
        title: "Link created",
        description: "Your property link has been created successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create link",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update an existing link
  const updateLinkMutation = useMutation({
    mutationFn: async ({ id, markupPercentage }: { id: number; markupPercentage: number }) => {
      const res = await apiRequest("PATCH", `agent/links/${id}`, { markupPercentage });
      // return await res.json();
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent/links"] });
      setIsEditingLink(false);
      setCurrentEditLink(null);
      toast({
        title: "Link updated",
        description: "Your property link has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update link",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete a link
  const deleteLinkMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `agent/links/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent/links"] });
      toast({
        title: "Link deleted",
        description: "Your property link has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete link",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Handle create link
  const handleCreateLink = () => {
    // Validate markup percentage
    const markup = parseFloat(markupPercentage);
    if (isNaN(markup) || markup < 0 || markup > 100) {
      toast({
        title: "Invalid markup",
        description: "Please enter a valid markup percentage between 0 and 100.",
        variant: "destructive",
      });
      return;
    }

    // Handle different link types
    if (linkType === "specific") {
      // For specific property links, a property must be selected
      if (!selectedProperty) {
        toast({
          title: "Select a property",
          description: "Please select a property to create a link for.",
          variant: "destructive",
        });
        return;
      }
      
      // Converting to number since it could be a string from URL parameters
      const propertyIdNum = parseInt(selectedProperty);
      
      // Log property ID for debugging
      console.log("Selected property ID:", propertyIdNum);
      
      // Ensure we have a valid property ID (not NaN)
      if (isNaN(propertyIdNum)) {
        toast({
          title: "Invalid property",
          description: "The selected property is invalid.",
          variant: "destructive",
        });
        return;
      }

      createLinkMutation.mutate({
        propertyId: propertyIdNum,
        markupPercentage: markup,
        allProperties: false
      });
    } else {
      // For universal links, we don't need a specific property
      createLinkMutation.mutate({
        markupPercentage: markup,
        allProperties: true
      });
    }
  };

  // Handle edit link
  const handleUpdateLink = () => {
    if (!currentEditLink) return;

    const markup = parseFloat(markupPercentage);
    if (isNaN(markup) || markup < 0 || markup > 100) {
      toast({
        title: "Invalid markup",
        description: "Please enter a valid markup percentage between 0 and 100.",
        variant: "destructive",
      });
      return;
    }

    updateLinkMutation.mutate({
      id: currentEditLink.id,
      markupPercentage: markup,
    });
  };

  // Copy link to clipboard
  const copyLinkToClipboard = (link: any) => {
    let url;
    if (link.allProperties) {
      // Universal link - no specific property ID
      url = `${window.location.origin}/properties/link/${link.linkCode}`;
    } else {
      // Individual property link - include property ID
      url = `${window.location.origin}/properties/${link.propertyId}/link/${link.linkCode}`;
    }
    
    navigator.clipboard.writeText(url).then(
      () => {
        toast({
          title: "Link copied",
          description: "The link has been copied to clipboard.",
        });
      },
      () => {
        toast({
          title: "Failed to copy",
          description: "Could not copy the link to clipboard.",
          variant: "destructive",
        });
      }
    );
  };

  // Prepare for editing
  const startEditLink = (link: any) => {
    setCurrentEditLink(link);
    setMarkupPercentage(link.markupPercentage.toString());
    setIsEditingLink(true);
  };

  // Find property by id
  const getPropertyById = (id: number) => {
    if (!properties) return null;
    return properties.find((p: any) => p.id === id);
  };

  // Loading state
  if (isLoadingLinks) {
    return (
      <AgentLayout>
        <Helmet>
          <title>Property Links | StayMiddle</title>
        </Helmet>
        <div className="container py-10">
          <h1 className="text-3xl font-bold mb-8">Property Links</h1>
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </AgentLayout>
    );
  }

  // Error state
  if (isLinksError) {
    return (
      <AgentLayout>
        <Helmet>
          <title>Property Links | StayMiddle</title>
        </Helmet>
        <div className="container py-10">
          <h1 className="text-3xl font-bold mb-8">Property Links</h1>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <h3 className="text-xl font-bold mb-2">Failed to load links</h3>
                <p className="text-muted-foreground mb-4">
                  There was an error loading your links. Please try again.
                </p>
                <Button
                  onClick={() => queryClient.invalidateQueries({ queryKey: ["agent/links"] })}
                >
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <Helmet>
        <title>Property Links | StayMiddle</title>
      </Helmet>
      <div className="container py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Property Links</h1>
            <p className="text-muted-foreground mt-1">
              Create and manage your custom property links
            </p>
          </div>
          {user?.status === 'pending' || user?.status === 'inactive' ? (
            <Button className="ml-auto cursor-not-allowed opacity-70" disabled title="Your account must be approved to create property links">
              Create New Link
            </Button>
          ) : (
            <Dialog open={isCreatingLink} onOpenChange={setIsCreatingLink}>
              <DialogTrigger asChild>
                <Button className="ml-auto">Create New Link</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create new property link</DialogTitle>
                  <DialogDescription>
                    Generate a unique link with your custom markup to share with clients.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label>Link Type</Label>
                    <div className="flex gap-4 mb-4">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="specificProperty"
                          name="linkType"
                          value="specific"
                          checked={linkType === "specific"}
                          onChange={() => setLinkType("specific")}
                          className="mr-2 h-4 w-4 text-primary"
                          title="Create link for a specific property"
                        />
                        <Label htmlFor="specificProperty" className="cursor-pointer text-sm font-normal">
                          Specific Property
                        </Label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="universalLink"
                          name="linkType"
                          value="universal"
                          checked={linkType === "universal"}
                          onChange={() => setLinkType("universal")}
                          className="mr-2 h-4 w-4 text-primary"
                          title="Create universal link for all properties"
                        />
                        <Label htmlFor="universalLink" className="cursor-pointer text-sm font-normal">
                          All Properties (Universal)
                        </Label>
                      </div>
                    </div>
                    
                    {linkType === "specific" && (
                      <>
                        <Label htmlFor="property">Select Property</Label>
                        <Select
                          value={selectedProperty}
                          onValueChange={setSelectedProperty}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a property" />
                          </SelectTrigger>
                          <SelectContent>
                            {isLoadingProperties ? (
                              <div className="flex justify-center items-center p-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                              </div>
                            ) : properties && properties.length > 0 ? (
                              properties.filter((p: any) => p.status === "active").map((property: any) => (
                                <SelectItem key={property.id} value={property.id.toString()}>
                                  {property.title} ({property.area})
                                </SelectItem>
                              ))
                            ) : (
                              <div className="text-sm p-2 text-center text-muted-foreground">
                                No active properties available
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                    
                    {linkType === "universal" && (
                      <div className="bg-muted/30 p-3 rounded-md border border-muted text-sm">
                        <p className="font-medium mb-1">Universal Link</p>
                        <p className="text-muted-foreground">
                          This link will work for all properties on StayMiddle. Your markup will apply to any 
                          property a client books through this link.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="markup">Markup percentage</Label>
                    <div className="flex items-center">
                      <Input
                        id="markup"
                        type="number"
                        min="0"
                        max="100"
                        value={markupPercentage}
                        onChange={(e) => setMarkupPercentage(e.target.value)}
                        className="w-full"
                      />
                      <span className="ml-2">%</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      This is how much you'll add to the base price (1-100%)
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsCreatingLink(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateLink}
                    disabled={createLinkMutation.isPending}
                  >
                    {createLinkMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Link"
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Edit Link Dialog */}
          <Dialog open={isEditingLink} onOpenChange={setIsEditingLink}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit property link</DialogTitle>
                <DialogDescription>
                  Update the markup percentage for this link.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="property">Property</Label>
                  {currentEditLink?.allProperties ? (
                    <div className="flex items-center gap-2">
                      <Input 
                        value="All Properties"
                        disabled
                      />
                      <Badge className="bg-primary/20 text-primary hover:bg-primary/30">Universal</Badge>
                    </div>
                  ) : (
                    <Input 
                      value={currentEditLink ? getPropertyById(currentEditLink.propertyId)?.title : ""}
                      disabled
                      placeholder="Property Title"
                      title="Property Title"
                    />
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="markup">Markup percentage</Label>
                  <div className="flex items-center">
                    <Input
                      id="markup"
                      type="number"
                      min="0"
                      max="100"
                      value={markupPercentage}
                      onChange={(e) => setMarkupPercentage(e.target.value)}
                      className="w-full"
                      placeholder="Markup Percentage"
                      title="Markup Percentage"
                    />
                    <span className="ml-2">%</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This is how much you'll add to the base price (1-100%)
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditingLink(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpdateLink}
                  disabled={updateLinkMutation.isPending}
                >
                  {updateLinkMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Link"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {links && links.length > 0 ? (
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableCaption>List of your custom property links</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Link Code</TableHead>
                    <TableHead>Markup</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {links.map((link: any) => {
                    const isUniversal = link.allProperties === true;
                    const property = !isUniversal ? getPropertyById(link.propertyId) : null;
                    return (
                      <TableRow key={link.id}>
                        <TableCell>
                          {isUniversal ? (
                            <div>
                              <div className="font-medium">All Properties</div>
                              <Badge className="mt-1 bg-primary/20 text-primary hover:bg-primary/30">Universal Link</Badge>
                            </div>
                          ) : (
                            <div>
                              <div className="font-medium">{property?.title || "Unknown Property"}</div>
                              <div className="text-sm text-muted-foreground">
                                {property?.area || ""}
                              </div>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline" className="font-mono">
                              {link.linkCode}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => copyLinkToClipboard(link)}
                              title="Copy link"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>{link.markupPercentage}%</TableCell>
                        <TableCell>
                          {formatDistance(
                            new Date(link.createdAt),
                            new Date(),
                            { addSuffix: true }
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => startEditLink(link)}
                            title="Edit link"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive"
                                title="Delete link"
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete this link. Any existing bookings
                                  made through this link will still be tracked, but the link will
                                  no longer work for new bookings.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteLinkMutation.mutate(link.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  {deleteLinkMutation.isPending ? "Deleting..." : "Delete"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <div className="mx-auto bg-muted rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Link className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-bold mb-2">No links yet</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Create your first property link with a custom markup percentage to share with clients.
                </p>
                <Dialog open={isCreatingLink} onOpenChange={setIsCreatingLink}>
                  <DialogTrigger asChild>
                    <Button>Create Your First Link</Button>
                  </DialogTrigger>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AgentLayout>
  );
}